import { useEffect, useState } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Mail, Phone, MapPin, Send } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";
const API = process.env.REACT_APP_BACKEND_URL;

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const inputCls = "w-full bg-white/70 border border-[#2c1f20]/15 rounded-sm px-4 py-3 text-sm text-[#2c1f20] placeholder:text-[#2c1f20]/35 focus:outline-none focus:border-[#6e4550]/60 focus:ring-2 focus:ring-[#6e4550]/10 transition-[border-color,box-shadow] duration-200";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact — Psychotherapist in Milton & Burlington | Haya Healing Psychotherapy";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Contact Haya Healing Psychotherapy — send a message to Momna Sajid, RP (Qualifying), or book a complimentary 15-minute consultation. In-person in Milton & Burlington, virtual across Ontario.");
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again, or email momna@hayahealingpsychotherapy.com directly.");
      }
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="px-6 md:px-14 pt-14 md:pt-20 pb-16 md:pb-20">
        <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
          <ArrowLeft size={14} /> Back home
        </motion.a>
        <Reveal i={1}><p className="eyebrow mt-8">Contact · Milton, Burlington &amp; Online Across Ontario</p></Reveal>
        <Reveal i={2}>
          <h1 data-testid="contact-heading" className="font-serif-display mt-4 text-[clamp(2.2rem,5vw,4rem)] leading-[1.08] font-normal">
            Send a <em className="text-[#6e4550]">message.</em>
          </h1>
        </Reveal>
        <Reveal i={3}>
          <p className="mt-6 max-w-2xl text-[#2c1f20]/75 leading-relaxed">
            For appointments, the fastest route is booking a complimentary 15-minute consultation through Jane App.
            For questions about services, fees, insurance, or fit, use the form below — your message goes directly
            to Momna's inbox and is typically answered within two business days.
          </p>
        </Reveal>
      </section>

      <section data-testid="contact-crisis-note" className="px-6 md:px-14 py-6 border-y border-[#6e4550]/30 bg-[#f4e6e2]/60">
        <p className="max-w-6xl text-sm text-[#2c1f20]/85 leading-relaxed">
          <span className="font-semibold">This form is not monitored for urgent concerns.</span> If you are in crisis, call or text <a href="tel:988" className="underline underline-offset-2 font-semibold text-[#6e4550]">9-8-8</a> (Suicide Crisis Helpline, 24/7) or call <a href="tel:911" className="underline underline-offset-2 font-semibold text-[#6e4550]">911</a> if there is immediate danger. <a href="/crisis-support" className="underline underline-offset-2 font-semibold text-[#6e4550] whitespace-nowrap">See all crisis lines →</a>
        </p>
      </section>

      <section className="px-6 md:px-14 py-16 md:py-20">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-7">
            {sent ? (
              <div data-testid="contact-success" className="bg-[#f4e6e2]/70 border border-[#6e4550]/25 rounded-md p-8 md:p-10">
                <h2 className="font-serif-display text-2xl md:text-3xl">Your message has been sent.</h2>
                <p className="mt-4 text-sm text-[#2c1f20]/75 leading-relaxed">
                  Thank you for reaching out — your message is on its way to Momna's inbox. You can expect a reply
                  within two business days. If you would prefer to book directly, you are welcome to schedule a
                  complimentary 15-minute consultation below.
                </p>
                <a href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid text-sm mt-6 inline-flex">Book a Consultation <ArrowUpRight size={15} /></a>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#2c1f20]/70 mb-2">Name *</label>
                    <input id="contact-name" data-testid="contact-name-input" required minLength={2} maxLength={120}
                      value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#2c1f20]/70 mb-2">Email *</label>
                    <input id="contact-email" data-testid="contact-email-input" type="email" required
                      value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#2c1f20]/70 mb-2">Phone <span className="normal-case font-normal text-[#2c1f20]/45">(optional)</span></label>
                  <input id="contact-phone" data-testid="contact-phone-input" type="tel" maxLength={40}
                    value={form.phone} onChange={set("phone")} placeholder="(___) ___-____" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#2c1f20]/70 mb-2">Message *</label>
                  <textarea id="contact-message" data-testid="contact-message-input" required minLength={10} maxLength={5000} rows={7}
                    value={form.message} onChange={set("message")}
                    placeholder="A brief note about what you are looking for is enough — please avoid including detailed clinical or personal information here."
                    className={inputCls + " resize-y"} />
                </div>
                <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off"
                  aria-hidden="true" className="absolute -left-[9999px] w-px h-px opacity-0" />
                <p className="text-xs text-[#2c1f20]/50 leading-relaxed">
                  Messages sent through this form are not encrypted end-to-end. Please keep clinical details brief — anything
                  sensitive can be discussed in session.
                </p>
                {error && <p data-testid="contact-error" className="text-sm text-[#6e4550] font-medium">{error}</p>}
                <button data-testid="contact-submit-button" type="submit" disabled={sending}
                  className="pill-btn pill-solid text-sm disabled:opacity-60 disabled:pointer-events-none">
                  {sending ? "Sending…" : <>Send Message <Send size={15} /></>}
                </button>
              </form>
            )}
          </Reveal>

          <Reveal i={1} className="md:col-span-5">
            <div className="bg-[#f1ece4]/50 border border-[#2c1f20]/10 rounded-md p-7 md:p-8 space-y-6">
              <div>
                <p className="eyebrow">Prefer to book directly?</p>
                <a data-testid="contact-book-button" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-outline text-sm mt-4 inline-flex">Book Initial Consultation <ArrowUpRight size={14} /></a>
              </div>
              <div className="border-t border-[#2c1f20]/10 pt-6 space-y-4 text-sm">
                <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2.5 text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} className="shrink-0" /> momna@hayahealingpsychotherapy.com</a>
                <a href="tel:+13656172135" className="flex items-center gap-2.5 text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} className="shrink-0" /> (365) 617-2135</a>
                <p className="flex items-start gap-2.5 text-[#2c1f20]/75"><MapPin size={15} className="shrink-0 mt-0.5" /> In-person in Milton &amp; Burlington · Virtual across Ontario</p>
              </div>
              <div className="border-t border-[#2c1f20]/10 pt-6">
                <p className="text-xs text-[#2c1f20]/55 leading-relaxed">
                  Sessions are offered in English and Urdu. Registered Psychotherapist (Qualifying), College of
                  Registered Psychotherapists of Ontario (CRPO).
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="px-6 md:px-14 py-16 md:py-24 bg-[#f1ece4]/50 border-t border-[#2c1f20]/10">
        <Reveal><p className="eyebrow">Begin here</p></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">Book a <em className="text-[#6e4550]">complimentary initial consultation.</em></h2></Reveal>
        <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
          <a data-testid="contact-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
          <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
          <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
        </Reveal>
        <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
      </footer>
    </div>
  );
}
